import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router';
import { fetchProfessionalProfile } from '~/actions';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BackButton, CardSpinner, FloatingContainer, Screen, ZStack } from '~/commonComponents';
import {
  CandidateStatus,
  CandidateStatusMapper,
  useGetCandidateOnsiteInterviewList,
  useGetCandidateProfessional,
} from '~/domain/PermanentJobs';
import { useAppSelector } from '~/hooks';
import { useTrackOnMount } from '~/hooks/useTrackOnMount';
import { fireNewPermCandidateEmailLinkClicked } from '~/posthogs/events';
import { OutlineCheckCircleIcon, SolidCheckCircleIcon, OutlineXCircleIcon } from '@tempmee/react-icons';
import { HirePopupConfirmation } from '../PermanentJobsV2/JobOverview/components/CandidateTabView/CandidateListTable/HirePopup';
import { RejectPopupConfirmation } from '../PermanentJobsV2/JobOverview/components/CandidateTabView/CandidateListTable/RejectPopup';
import { ProfessionalProfile } from './components';
import { ExperienceOverview, OnsiteInterviewsPanel } from './containers';
import { useHireCandidate } from './hooks/useHireCandidate';
import { useRejectCandidate } from './hooks/useRejectCandidate';
import { TCandidateOverviewPageRoutes } from './Types';

const UTM_SOURCE_PARAM = 'utm_source';
const UTM_MEDIUM_PARAM = 'utm_medium';
const EMAIL_SOURCE = 'email';
const DIRECT_APPLY_MEDIUM = 'direct_apply';

export const CandidateOverview = () => {
  const dispatch = useDispatch();

  const { jobPostingId, professionalId } = useParams<TCandidateOverviewPageRoutes>();

  const [searchParams] = useSearchParams();

  // The "Review Candidate" CTA in the "Meet your newest candidate" email lands
  // the office directly on this route (Branch `$desktop_url`), tagged with
  // utm_source=email & utm_medium=direct_apply. Fire the landing event once so
  // PostHog can measure email-driven candidate reviews (GOTU-4403).
  const isEmailLink =
    searchParams.get(UTM_SOURCE_PARAM) === EMAIL_SOURCE &&
    searchParams.get(UTM_MEDIUM_PARAM) === DIRECT_APPLY_MEDIUM;

  useTrackOnMount(fireNewPermCandidateEmailLinkClicked, { enabled: isEmailLink });

  const professional = useAppSelector((state) => state.user.professionalProfile);

  const { data: candidate, isInitialLoading: isGetCandidateProfessionalLoading } =
    useGetCandidateProfessional(
      {
        postingId: jobPostingId || '',
        professionalId: professionalId || '',
      },
      { enabled: !!jobPostingId && !!professionalId },
    );

  const { data, isInitialLoading: isGetCandidateOnsiteInterviewListLoading } =
    useGetCandidateOnsiteInterviewList(
      {
        postingId: jobPostingId || '',
        professionalId: professionalId || '',
      },
      {
        enabled: !!jobPostingId && !!professionalId,
      },
    );

  const {
    canHire,
    controls: controlsHire,
    isOpen: isHireOpen,
    handleHireClick,
  } = useHireCandidate({
    candidate,
    hasCompletedInterview: data?.hasCompletedInterviews,
  });

  const {
    canReject,
    controls: controlsReject,
    isOpen: isRejectOpen,
    handleRejectClick,
  } = useRejectCandidate({ candidate });

  const hasWorkedWith = useMemo(() => {
    const obj = {
      dso: false,
      office: false,
    };

    data?.groups.action_needed.forEach((id) => {
      const it = data.maps[id];

      if (it.counterOffer?.hasWorkedWithDSO) {
        obj.dso = true;
      }

      if (it.counterOffer?.hasWorkedWithOffice) {
        obj.office = true;
      }
    });

    return obj;
  }, [data?.groups.action_needed, data?.maps]);

  useEffect(() => {
    if (professionalId) dispatch(fetchProfessionalProfile(professionalId));
  }, [dispatch, professionalId]);

  if (
    !professional ||
    isGetCandidateProfessionalLoading ||
    isGetCandidateOnsiteInterviewListLoading
  ) {
    return <CardSpinner />;
  }

  const officeStatus = data?.officeStatus ?? candidate?.officeStatus;

  return (
    <>
      <Screen>
        <FloatingContainer borderRadius={0} flex={1} paddingBlock={54} paddingInline={48}>
          <Row align="center" justify="center" style={{ position: 'relative' }}>
            <ZStack left={0}>
              <BackButton />
            </ZStack>
            <Column>
              <Typography as="span" bold size="heading_2" textAlign="center">
                Candidate Overview
              </Typography>
              {officeStatus && (
                <Typography
                  as="span"
                  bold
                  size="body_large"
                  style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    gap: 4,
                    justifyContent: 'center',
                  }}
                >
                  Status:{' '}
                  <Typography
                    as="span"
                    color={CandidateStatusMapper.getColor(officeStatus)}
                    size="body_large"
                  >
                    {CandidateStatusMapper.toHumanReadable(officeStatus).toUpperCase()}
                  </Typography>
                  {officeStatus === CandidateStatus.Hired && <SolidCheckCircleIcon />}
                </Typography>
              )}
            </Column>
          </Row>

          <Row gap={5} style={{ marginTop: 48 }}>
            <Column className="flex-1">
              <ProfessionalProfile
                disableNavigation
                hasWorkedWithDSO={hasWorkedWith.dso}
                hasWorkedWithOffice={hasWorkedWith.office}
                professional={professional}
              />
            </Column>
            <Row className="flex-1" align="center" justify="center" gap={4}>
              <button
                className="shadow-xs hover__shadow-sm"
                data-cy="candidate-action-reject"
                disabled={!canReject}
                type="button"
                style={{
                  alignItems: 'center',
                  background: colors.neutral[100],
                  borderRadius: 48,
                  cursor: canReject ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  gap: 6,
                  height: 48,
                  justifyContent: 'center',
                  opacity: canReject ? 1 : 0.5,
                  paddingBlock: 14,
                  paddingInline: 16,
                  width: 140,
                }}
                onClick={handleRejectClick}
              >
                <OutlineXCircleIcon color={colors.primary[500]} />
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  Reject
                </Typography>
              </button>
              <button
                className="shadow-xs hover__shadow-sm"
                data-cy="candidate-action-hire"
                disabled={!canHire}
                type="button"
                style={{
                  alignItems: 'center',
                  background: colors.primary[500],
                  borderRadius: 48,
                  cursor: canHire ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  gap: 6,
                  height: 48,
                  justifyContent: 'center',
                  opacity: canHire ? 1 : 0.5,
                  paddingBlock: 14,
                  paddingInline: 16,
                  width: 140,
                }}
                onClick={handleHireClick}
              >
                <OutlineCheckCircleIcon color={colors.shades[0]} />
                <Typography as="span" bold color={colors.shades[0]} size="body_large">
                  Hire
                </Typography>
              </button>
            </Row>
          </Row>

          <Row gap={5} style={{ marginTop: 18 }}>
            <Column className="flex-1">
              <ExperienceOverview
                hasWorkedWithDSO={hasWorkedWith.dso}
                hasWorkedWithOffice={hasWorkedWith.office}
                postingId={jobPostingId}
                professional={professional}
              />
            </Column>
            <Column className="flex-1">
              {data && (
                <OnsiteInterviewsPanel
                  postingId={jobPostingId ?? ''}
                  groups={data.groups}
                  maps={data.maps}
                  officeId={candidate?.officeId || ''}
                  officeStatus={data.officeStatus}
                  professionalId={professional.id}
                  professionalName={professional.full_name || ''}
                />
              )}
            </Column>
          </Row>
        </FloatingContainer>
      </Screen>

      {isHireOpen && !!candidate && (
        <HirePopupConfirmation
          candidateId={candidate.id}
          controls={controlsHire}
          professionalName={professional.full_name}
        />
      )}

      {isRejectOpen && !!candidate && (
        <RejectPopupConfirmation
          candidateId={candidate.id}
          controls={controlsReject}
          hasScheduledInterview={data?.hasScheduledInterview}
          professionalName={professional.full_name}
        />
      )}
    </>
  );
};
